import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, Building2, Users, Download } from 'lucide-react';

interface SkillCompanyStat {
  skill_id: string;
  company_id: string;
  candidate_count: number;
  skills: { name: string; type: string | null };
  companies: { name: string };
}

const SkillCompanyReport = () => {
  const [stats, setStats] = useState<SkillCompanyStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('skill_company_stats')
        .select(`
          skill_id,
          company_id,
          candidate_count,
          skills(name, type),
          companies(name)
        `)
        .order('candidate_count', { ascending: false })
        .limit(100);

      if (error) throw error;
      setStats((data as any) || []);
    } catch (error) {
      console.error('Error fetching skill company stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStats = stats.filter(stat => 
    stat.skills?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stat.companies?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportToCSV = () => {
    const headers = ['Skill', 'Type', 'Company', 'Candidate Count'];
    const rows = filteredStats.map(stat => [
      stat.skills?.name || '',
      stat.skills?.type || '',
      stat.companies?.name || '',
      stat.candidate_count
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skill-company-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group by company for summary
  const companySkillCounts = stats.reduce((acc, stat) => {
    const companyName = stat.companies?.name || 'Unknown';
    if (!acc[companyName]) {
      acc[companyName] = { skills: 0, candidates: 0 };
    }
    acc[companyName].skills++;
    acc[companyName].candidates += stat.candidate_count;
    return acc;
  }, {} as Record<string, { skills: number; candidates: number }>);

  const topCompanies = Object.entries(companySkillCounts)
    .sort((a, b) => b[1].candidates - a[1].candidates)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{Object.keys(companySkillCounts).length}</p>
              <p className="text-sm text-muted-foreground">Companies</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {stats.reduce((sum, s) => sum + s.candidate_count, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Skill Instances</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <h4 className="font-medium text-sm mb-2">Top Companies</h4>
          <div className="space-y-1">
            {topCompanies.map(([name, data]) => (
              <div key={name} className="flex justify-between text-sm">
                <span className="truncate">{name}</span>
                <Badge variant="secondary">{data.skills} skills</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Search and Export */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search skills or companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={exportToCSV}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Data Table */}
      {filteredStats.length === 0 ? (
        <Card className="p-8 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No skill-company associations found</p>
          <p className="text-sm text-muted-foreground">
            Skills will be tracked when candidates are added with experience data
          </p>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="text-right">Candidates</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStats.map((stat, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{stat.skills?.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{stat.skills?.type || 'unknown'}</Badge>
                  </TableCell>
                  <TableCell>{stat.companies?.name}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{stat.candidate_count}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default SkillCompanyReport;